import {
  type ComponentType,
  createContext,
  useContext,
  useEffect,
  useRef,
} from 'react'

import type {
  DynamicIslandActivity,
  DynamicIslandRenderProps,
} from './dynamicIslandTypes'

export type {
  DynamicIslandAccent,
  DynamicIslandAction,
  DynamicIslandActivity,
  DynamicIslandCompactPreview,
  DynamicIslandExpandedPreview,
  DynamicIslandLifecycle,
  DynamicIslandRenderProps,
  DynamicIslandSlot,
} from './dynamicIslandTypes'

export type RegisterOptions = {
  priority?: number
}

type DynamicIslandContextValue = {
  expanded: boolean
  setExpanded: (next: boolean) => void
  selectedId: string | null
  setSelectedId: (id: string) => void
  detailActivityId: string | null
  openActivityDetail: (id: string) => void
  closeActivityDetail: () => void
  activities: DynamicIslandActivity[]
  reportActivity: (activity: DynamicIslandActivity) => void
  clearActivity: (id: string) => void
  notifyContentChange: () => void
}

export const DynamicIslandContext = createContext<DynamicIslandContextValue | null>(null)

export function useDynamicIsland() {
  const ctx = useContext(DynamicIslandContext)
  if (!ctx) throw new Error('useDynamicIsland 须在 DynamicIslandProvider 内使用')
  return ctx
}

/** 全局 Provider 内上报一条后台活动（卸载或 activity=null 时自动清除） */
export function useReportDynamicIslandActivity(activity: DynamicIslandActivity | null) {
  const { reportActivity, clearActivity, notifyContentChange } = useDynamicIsland()
  const id = activity?.id ?? null
  const prevIdRef = useRef<string | null>(null)
  const prevSnapshotRef = useRef<string | null>(null)

  useEffect(() => {
    if (!activity || !id) {
      if (prevIdRef.current) {
        clearActivity(prevIdRef.current)
        prevIdRef.current = null
      }
      return
    }
    prevIdRef.current = id
    reportActivity(activity)
    return () => clearActivity(id)
  }, [id, activity, reportActivity, clearActivity])

  useEffect(() => {
    if (!id || !activity) {
      prevSnapshotRef.current = null
      return
    }
    const snapshot = JSON.stringify({
      lifecycle: activity.lifecycle,
      priority: activity.priority,
      compact: activity.compact,
      expanded: activity.expanded,
      actions: activity.actions.map((a) => ({
        id: a.id,
        label: a.label,
        variant: a.variant,
        disabled: a.disabled,
      })),
    })
    if (prevSnapshotRef.current === snapshot) return
    prevSnapshotRef.current = snapshot
    notifyContentChange()
  }, [activity, id, notifyContentChange])
}

/** 同时上报多条活动（如多个工作流运行） */
export function useReportDynamicIslandActivities(activities: DynamicIslandActivity[]) {
  const { reportActivity, clearActivity, notifyContentChange } = useDynamicIsland()
  const idsKey = activities.map((item) => item.id).join('\0')
  const snapshot = JSON.stringify(
    activities.map((item) => ({
      id: item.id,
      lifecycle: item.lifecycle,
      priority: item.priority,
      compact: item.compact,
      expanded: item.expanded,
      actions: item.actions.map((action) => ({
        id: action.id,
        label: action.label,
        variant: action.variant,
        disabled: action.disabled,
      })),
    })),
  )
  const prevSnapshotRef = useRef<string | null>(null)

  useEffect(() => {
    for (const item of activities) reportActivity(item)
    return () => {
      for (const item of activities) clearActivity(item.id)
    }
  }, [idsKey, snapshot, activities, reportActivity, clearActivity])

  useEffect(() => {
    if (activities.length === 0) {
      prevSnapshotRef.current = null
      return
    }
    if (prevSnapshotRef.current === snapshot) return
    prevSnapshotRef.current = snapshot
    notifyContentChange()
  }, [activities.length, snapshot, notifyContentChange])
}

/** @deprecated 请改用 {@link useReportDynamicIslandActivity} */
export function useDynamicIslandSlot<P extends object>(
  _id: string,
  _props: P | null,
  _Component: ComponentType<P & DynamicIslandRenderProps>,
  _options?: RegisterOptions,
) {
  if (import.meta.env.DEV) {
    console.warn('useDynamicIslandSlot 已废弃，请改用 useReportDynamicIslandActivity')
  }
}
